import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
  IconAudioRecord,
  IconSupport,
  IconAdd,
  IconDashboard,
  IconMail,
  IconSites,
  IconTools,
  IconBag,
  IconChevronV3,
  IconArrowUpRight,
  IconClose,
  IconCheck,
  IconLoading,
} from 'gatorcomponents';
import { useResize } from '@/hooks/useResize';
import { useGatorChat } from '@/hooks/useGatorChat';
import useLocale from '@/hooks/useLocale';
import { MOBILE_BREAKPOINT } from '@/utils/breakpoints.constants';
import { OPTIONS } from '@/components/Layout/Menu/HeaderV2/components/NewAiHeaderButton/NewAiHeaderButton.types';
import GatorPresentationModal from '@/components/Modal/GatorPresentationModal';
import gatorAvatar from '@/media/gatorAi/gator-32.svg';
import gatorRecordingAvatar from '@/media/gatorAi/gator-recording.png';
import type {
  GatorTopbarHeaderProps,
  ChipConfig,
  TopbarHeaderLocale,
} from './GatorTopbarHeader.types';
import * as Styles from './GatorTopbarHeader.styles';

const COMPACT_CHIP_KEYS = ['site', 'email', 'support'];

const CHIPS_EXPANDED: ChipConfig[] = [
  {
    id: 'site', action: 'newSite', optionLabel: OPTIONS.CREATE_SITE, Icon: IconDashboard,
  },
  {
    id: 'email', action: 'email', optionLabel: OPTIONS.CREATE_EMAIL, Icon: IconMail,
  },
  {
    id: 'support', action: 'diagnostic', optionLabel: OPTIONS.SUPPORT, Icon: IconSupport,
  },
  {
    id: 'domainPurchase', action: 'domainPurchase', optionLabel: OPTIONS.DOMAIN_PURCHASE, Icon: IconAdd,
  },
  {
    id: 'domainConfig', action: 'domainConfig', optionLabel: OPTIONS.CONFIGURE_DOMAIN, Icon: IconSites,
  },
  {
    id: 'checkAccount', action: 'checkAccount', optionLabel: OPTIONS.CHECK_ACCOUNT, Icon: IconTools,
  },
  {
    id: 'contractProduct', action: 'contractProduct', optionLabel: OPTIONS.CONTRACT_PRODUCT, Icon: IconBag, spanFull: true,
  },
];

const GatorTopbarHeader = ({
  hideOnMobile, hideOnDesktop, overlayZIndex, onFocusChange,
}: GatorTopbarHeaderProps) => {
  const { width } = useResize();
  const isMobile = width < MOBILE_BREAKPOINT;

  const {
    handleSubmit,
    handleChipClick,
    handleSuggestionPromptClick,
    canShowAudio,
    handleStartRecording,
    handleStopRecording,
    isRecording,
    loadingTranscription,
    formattedTime,
  } = useGatorChat();
  const { aichat } = useLocale() as TopbarHeaderLocale;
  const topbarHeader = aichat?.topbarHeader;

  const [value, setValue] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [isPresentationModalOpen, setIsPresentationModalOpen] = useState(false);
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0, width: 0 });
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isExpanded && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isExpanded]);

  const handleExpand = useCallback(() => {
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      setPanelPos({ top: rect.top, left: rect.left, width: rect.width });
    }
    setIsExpanded(true);
    onFocusChange && onFocusChange(true);
  }, [onFocusChange]);

  const handleClose = useCallback(() => {
    setIsExpanded(false);
    onFocusChange && onFocusChange(false);
  }, [onFocusChange]);

  useEffect(() => {
    if (!isExpanded) return undefined;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isExpanded, handleClose]);

  useEffect(() => {
    if (!isExpanded) return undefined;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isExpanded]);

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(event.target.value);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Enter' || event.shiftKey) return;
    const trimmed = value.trim();
    if (trimmed.length === 0) return;
    event.preventDefault();
    handleSubmit(trimmed);
    setValue('');
    handleClose();
  };

  const handleGridChipClick = (chip: ChipConfig, message?: string) => {
    handleChipClick({ action: chip.action, optionLabel: chip.optionLabel }, message);
    handleClose();
  };

  const handleSuggestionClick = (prompt: string) => {
    handleSuggestionPromptClick(prompt);
    handleClose();
  };

  const handleLearnMoreClick = () => {
    handleClose();
    setIsPresentationModalOpen(true);
  };

  const renderCompactInput = (idPrefix: string, withRef: boolean, fixedWidth = false) => (
    <Styles.CompactInput $fixedWidth={fixedWidth} $isRecording={isRecording} $panel={withRef}>
      {isRecording ? (
        <Styles.RecordingContent data-testid={`${idPrefix}-recording`}>
          <Styles.RecordingLeft>
            <Styles.RecordingAvatar>
              <img src={gatorRecordingAvatar} alt="" />
            </Styles.RecordingAvatar>
            <Styles.RecordingLabel>
              {loadingTranscription
                ? topbarHeader?.audio?.transcribingLabel
                : topbarHeader?.audio?.recordingLabel}
            </Styles.RecordingLabel>
          </Styles.RecordingLeft>
          <Styles.RecordingRight>
            <Styles.RecordingTime data-testid={`${idPrefix}-recording-time`}>
              {formattedTime}
            </Styles.RecordingTime>
            <Styles.RecordingActionButton
              type="button"
              $variant="cancel"
              onClick={() => handleStopRecording({ cancelRecording: true })}
              disabled={loadingTranscription}
              data-testid={`${idPrefix}-recording-cancel`}
            >
              <IconClose size="sm" color="highPure" />
            </Styles.RecordingActionButton>
            <Styles.RecordingActionButton
              type="button"
              $variant="send"
              onClick={() => handleStopRecording({ cancelRecording: false })}
              disabled={loadingTranscription}
              data-testid={`${idPrefix}-recording-send`}
            >
              {loadingTranscription ? <IconLoading size="sm" /> : <IconCheck size="sm" color="highPure" />}
            </Styles.RecordingActionButton>
          </Styles.RecordingRight>
        </Styles.RecordingContent>
      ) : (
        <>
          <Styles.Avatar>
            <img src={gatorAvatar} alt="" />
          </Styles.Avatar>
          <Styles.InputField
            ref={withRef ? inputRef : undefined}
            rows={1}
            value={value}
            placeholder={(isMobile ? topbarHeader?.placeholderMobile : topbarHeader?.placeholder) ?? ''}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={handleExpand}
            data-testid={`${idPrefix}-input`}
          />
          {canShowAudio && (
            <Styles.AudioButton
              type="button"
              onClick={handleStartRecording}
              data-testid={`${idPrefix}-audio-button`}
            >
              <IconAudioRecord size="sm2" color="lowMedium" />
            </Styles.AudioButton>
          )}
        </>
      )}
    </Styles.CompactInput>
  );

  if (hideOnMobile && isMobile) return null;
  if (hideOnDesktop && !isMobile) return null;

  return (
    <>
      <Styles.CenteredWrapper>
        <Styles.Wrapper ref={wrapperRef}>
          <Styles.TopbarContainer $hidden={isExpanded} data-testid="gator-topbar-header">
            {renderCompactInput('gator-topbar-header', false, !isRecording)}
            {!isRecording && (
              <Styles.SuggestedOptions>
                {isMobile && (
                  <Styles.SuggestionChip
                    type="button"
                    onClick={() => handleChipClick(
                      { action: 'newSite', optionLabel: OPTIONS.CREATE_SITE },
                      topbarHeader?.chips?.site?.message,
                    )}
                    data-testid="gator-topbar-header-chip-site"
                  >
                    <IconDashboard size="sm2" color="highPure" />
                    <Styles.SuggestionChipLabel>
                      {topbarHeader?.chips?.site?.label}
                    </Styles.SuggestionChipLabel>
                  </Styles.SuggestionChip>
                )}
                <Styles.SuggestionChip
                  type="button"
                  onClick={() => handleChipClick(
                    { action: 'diagnostic', optionLabel: OPTIONS.SUPPORT },
                    topbarHeader?.chips?.support?.message,
                  )}
                  data-testid="gator-topbar-header-chip-support"
                >
                  <IconSupport size="sm2" color="highPure" />
                  <Styles.SuggestionChipLabel>
                    {topbarHeader?.chips?.support?.label}
                  </Styles.SuggestionChipLabel>
                </Styles.SuggestionChip>
                <Styles.SuggestionChipPlus
                  type="button"
                  onClick={handleExpand}
                  data-testid="gator-topbar-header-chip-plus"
                >
                  <IconAdd size="sm2" color="highPure" />
                </Styles.SuggestionChipPlus>
              </Styles.SuggestedOptions>
            )}
            <Styles.VersionTag data-testid="gator-topbar-header-version-tag">
              <Styles.VersionTagText>2.0</Styles.VersionTagText>
            </Styles.VersionTag>
          </Styles.TopbarContainer>
        </Styles.Wrapper>
      </Styles.CenteredWrapper>

      {isExpanded && createPortal(
        <>
          <Styles.Overlay
            $zIndex={overlayZIndex}
            onClick={handleClose}
            data-testid="gator-topbar-header-overlay"
          />
          {!isMobile && <Styles.EdgesGlow $zIndex={overlayZIndex} />}
          <Styles.ExpandedPanel
            data-testid="gator-topbar-header-panel"
            $top={panelPos.top}
            $left={panelPos.left}
            $width={panelPos.width}
            $isMobile={isMobile}
          >
            {renderCompactInput('gator-topbar-header-panel', true)}
            <Styles.ChipsGrid>
              {CHIPS_EXPANDED.map((chip) => {
                const chipLocale = COMPACT_CHIP_KEYS.includes(chip.id)
                  ? topbarHeader?.chips?.[chip.id]
                  : topbarHeader?.expandedChips?.[chip.id];
                return (
                  <Styles.SuggestionChip
                    key={chip.id}
                    type="button"
                    $spanFull={chip.spanFull}
                    onClick={() => handleGridChipClick(chip, chipLocale?.message)}
                    data-testid={`gator-topbar-header-panel-chip-${chip.id}`}
                  >
                    <chip.Icon size="sm" color="highPure" />
                    <Styles.SuggestionChipLabel>
                      {chipLocale?.label}
                    </Styles.SuggestionChipLabel>
                  </Styles.SuggestionChip>
                );
              })}
            </Styles.ChipsGrid>
            <Styles.SuggestionsLabel>
              {topbarHeader?.suggestionsLabel}
            </Styles.SuggestionsLabel>
            <Styles.SuggestionsList>
              {(topbarHeader?.suggestions ?? []).map((prompt, index) => (
                <React.Fragment key={prompt}>
                  {index > 0 && <Styles.SuggestionDivider />}
                  <Styles.SuggestionItem
                    type="button"
                    onClick={() => handleSuggestionClick(prompt)}
                    data-testid={`gator-topbar-header-suggestion-${index}`}
                  >
                    <Styles.SuggestionItemText>{prompt}</Styles.SuggestionItemText>
                    <IconArrowUpRight size="sm" color="highPure" />
                  </Styles.SuggestionItem>
                </React.Fragment>
              ))}
            </Styles.SuggestionsList>
            <Styles.LearnMoreBar
              type="button"
              onClick={handleLearnMoreClick}
              data-testid="gator-topbar-header-learn-more"
            >
              {topbarHeader?.learnMoreLabel}
              <IconChevronV3 direction="right" size="sm" color="highPure" />
            </Styles.LearnMoreBar>
          </Styles.ExpandedPanel>
        </>,
        document.getElementById('navMenu') || document.body,
      )}

      <GatorPresentationModal
        open={isPresentationModalOpen}
        onClose={() => setIsPresentationModalOpen(false)}
      />
    </>
  );
};

export default GatorTopbarHeader;
